/* eslint-disable no-useless-escape */
import React, { useEffect, useRef, useState } from 'react';
import Globe, { GlobeMethods } from 'react-globe.gl';
import * as satellite from 'satellite.js';
import * as THREE from 'three';

// --- Constants ---
const EARTH_RADIUS_KM = 6371;
const ORBIT_POINTS_COUNT = 50;
const TIME_STEP_ORBIT_CALC = new Date();
const radiansToDegrees = (r: number) => (r * 180) / Math.PI;
const degreesToRadians = (d: number) => (d * Math.PI) / 180;

// --- Constellation Schema ---
interface ConstellationCode {
  name: string;
  subtype: 'Delta' | 'Start';
  inclinationDeg: number;
  apogeeKm: number;
  perigeeKm: number;
  argOfPerigeeDeg: number;
  numPlanes: number;
  numSatellites: number;
  f: number;
  nuOffsetDeg: number;
  color: string;
}

// --- Input from user ---
interface ConstellationGlobeProps {
  userDefinedConstellations?: {
    id: string;
    text: string;
    color: string;
  }[];
}

// --- Processed Output Types ---
interface SatellitePoint {
  lat: number;
  lng: number;
  alt: number;
}

interface ProcessedSatellite extends SatellitePoint {
  id: string;
  constellationName: string;
  color: string;
  nameForLabel: string;
  orbitArcPoints: SatellitePoint[];
}

interface OrbitRenderData {
  id: string;
  points: SatellitePoint[];
  color: string;
}

// --- Convert Keplerian elements to ECI ---
function calculateEciPosition(
  a: number,
  e: number,
  iRad: number,
  raanRad: number,
  argPerigeeRad: number,
  trueAnomalyRad: number
): satellite.EciVec3<number> {
  const r = a * (1 - e * e) / (1 + e * Math.cos(trueAnomalyRad));
  const x_pqw = r * Math.cos(trueAnomalyRad);
  const y_pqw = r * Math.sin(trueAnomalyRad);
  const z_pqw = 0;

  const cosRAAN = Math.cos(raanRad);
  const sinRAAN = Math.sin(raanRad);
  const cosArgP = Math.cos(argPerigeeRad);
  const sinArgP = Math.sin(argPerigeeRad);
  const cosI = Math.cos(iRad);
  const sinI = Math.sin(iRad);

  const x_eci =
    x_pqw * (cosRAAN * cosArgP - sinRAAN * sinArgP * cosI) -
    y_pqw * (cosRAAN * sinArgP + sinRAAN * cosArgP * cosI);
  const y_eci =
    x_pqw * (sinRAAN * cosArgP + cosRAAN * sinArgP * cosI) -
    y_pqw * (sinRAAN * sinArgP - cosRAAN * cosArgP * cosI);
  const z_eci = x_pqw * sinArgP * sinI + y_pqw * cosArgP * sinI;

  return { x: x_eci, y: y_eci, z: z_eci };
}

function getLatLngAlt(eciPos: satellite.EciVec3<number>, gmst: number): SatellitePoint {
  const geoCoords = satellite.eciToGeodetic(eciPos, gmst);
  return {
    lat: radiansToDegrees(geoCoords.latitude),
    lng: radiansToDegrees(geoCoords.longitude),
    alt: geoCoords.height / EARTH_RADIUS_KM,
  };
}

// --- Generate Satellite Positions ---
const generateWalkerConstellation = (definition: ConstellationCode): ProcessedSatellite[] => {
  const {
    name,
    subtype,
    inclinationDeg,
    apogeeKm,
    perigeeKm,
    argOfPerigeeDeg,
    numPlanes,
    numSatellites,
    f,
    nuOffsetDeg,
    color,
  } = definition;

  const inclinationRad = degreesToRadians(inclinationDeg);
  const argPerigeeRad = degreesToRadians(argOfPerigeeDeg);
  const r_apogee = apogeeKm + EARTH_RADIUS_KM;
  const r_perigee = perigeeKm + EARTH_RADIUS_KM;
  const a = (r_apogee + r_perigee) / 2;
  const e = (r_apogee - r_perigee) / (r_apogee + r_perigee);

  const satsPerPlane = Math.floor(numSatellites / numPlanes);
  const gmst = satellite.gstime(TIME_STEP_ORBIT_CALC);
  const processedSats: ProcessedSatellite[] = [];

  var totalIDx: number = 0;
  for (let plane = 0; plane < numPlanes; plane++) {
    const raanDeg =
      subtype === 'Delta'
        ? (360 / numPlanes) * plane
        : (180 / numPlanes) * plane; // Star only covers half sphere
    const raanRad = degreesToRadians(raanDeg);

    // Orbit Path
    const orbitArcPoints: SatellitePoint[] = [];
    for (let i = 0; i < ORBIT_POINTS_COUNT; i++) {
      const trueAnomalyRad = (i / ORBIT_POINTS_COUNT) * 2 * Math.PI;
      const posEci = calculateEciPosition(a, e, inclinationRad, raanRad, argPerigeeRad, trueAnomalyRad);
      orbitArcPoints.push(getLatLngAlt(posEci, gmst));
    }

    for (let sIdx = 0; sIdx < satsPerPlane; sIdx++) {
      totalIDx++;
      const planeOffsetDeg = (plane * f * 360) / numSatellites;
      const nuDeg = ((360 / satsPerPlane) * sIdx + planeOffsetDeg + nuOffsetDeg) % 360;
      const trueAnomalyRad = degreesToRadians(nuDeg);

      const posEci = calculateEciPosition(a, e, inclinationRad, raanRad, argPerigeeRad, trueAnomalyRad);
      const geoPos = getLatLngAlt(posEci, gmst);

      const satId = `P${plane + 1}-S${sIdx + 1}-N${totalIDx}`;

      // console.log("Pushing sat with color", color, "and ID", satId);

      processedSats.push({
        ...geoPos,
        id: satId,
        constellationName: name,
        color,
        nameForLabel: `ID: ${satId}|${name}|Alt ~${(a - EARTH_RADIUS_KM).toFixed(0)}km|Inc ${inclinationDeg}°`,
        orbitArcPoints,
      });
    }
  }

  return processedSats;
};

// --- Parse User Input Constellation String ---
function parseConstellationString(inputTextRaw: string, color: string): ConstellationCode[] {
  const results: ConstellationCode[] = [];

  try {
    const entries = inputTextRaw.split('+');
    entries.forEach((inputText, idx) => {
      const regex_code = /^([DS](?:\/[\d\.]+)?):([\d\.]+(?:\/[\d\.]+)?(?:\/[\d\.]+)?):([\d\.]+):([\d\.]+)(?:\/([\d\.]+))?(?:\/([\d\.]+))?(?:\/([\d\.]+))?$/i;

      const match = inputText.match(regex_code);
      if (!match) return;

      // Walker part
      let subtypeStr = match[1];
      let subtypeMatch = subtypeStr.match(/^([DS])(?:\/([\d\.]+))?$/i);
      if (!subtypeMatch) return;

      const subtype = subtypeMatch[1] === 'S' ? 'Start' : 'Delta';
      const raanOffset = parseFloat(subtypeMatch[2] || '0');

      // Altitude part
      const apogeeKm = parseFloat(match[2].split('/')[0]);
      const perigeeKm = parseFloat(match[2].split('/')[1] || match[2].split('/')[0]);
      const argOfPerigeeDeg = parseFloat(match[2].split('/')[2] || '0');

      // Inclination
      const inclinationDeg = parseFloat(match[3]);

      // Plane params
      const n_sats = parseInt(match[4]);
      const n_planes = parseInt(match[5] || '1');
      const f_param = parseInt(match[6] || '0');
      const nu_offset_deg = parseInt(match[7] || '0');

      results.push({
        name: `C${idx + 1}`,
        subtype,
        inclinationDeg,
        apogeeKm,
        perigeeKm,
        argOfPerigeeDeg,
        numPlanes: n_planes,
        numSatellites: n_sats,
        f: f_param,
        nuOffsetDeg: nu_offset_deg + raanOffset,
        color: color,
      });
    });
    return results;
  } catch (err) {
    console.error('Error parsing constellation:', err);
    return [];
  }
}

// --- Main Component ---
const ConstellationGlobe: React.FC<ConstellationGlobeProps> = ({ userDefinedConstellations }) => {
  const globeEl = useRef<GlobeMethods | undefined>();
  const [satellites, setSatellites] = useState<ProcessedSatellite[]>([]);
  const [orbits, setOrbits] = useState<OrbitRenderData[]>([]);

  useEffect(() => {
    if (!userDefinedConstellations) return;

    let allSatellites: ProcessedSatellite[] = [];
    const orbitMap = new Map<string, OrbitRenderData>();

    userDefinedConstellations.forEach(({ text, color }, idx) => {
      const defs = parseConstellationString(text, color);

      defs.forEach((def, dIdx) => {
        def.color = color;
        def.name = `C${idx + 1}-${dIdx + 1}`;

        const sats = generateWalkerConstellation(def);
        allSatellites.push(...sats);

        for (let p = 0; p < def.numPlanes; p++) {
          const key = `${def.name}_P${p}`;
          if (!orbitMap.has(key)) {
            const sampleSat = sats.find((s) => s.id.startsWith(`P${p + 1}-`));
            if (sampleSat?.orbitArcPoints?.length) {
              orbitMap.set(key, {
                id: key,
                points: sampleSat.orbitArcPoints,
                color,
              });
            }
          }
        }
      });
    });

    setSatellites(allSatellites);
    setOrbits(Array.from(orbitMap.values()));
  }, [userDefinedConstellations]);

  return (
    <div style={{ height: '100%' }}>
      <Globe
        ref={globeEl}
        globeImageUrl="/assets/earth-blue-marble-tim1.jpg"
        bumpImageUrl="/assets/earth-topology.png"
        backgroundImageUrl="/assets/night-sky-upscaled.png"
        showAtmosphere={true}
        atmosphereColor="lightblue"
        atmosphereAltitude={0.27}
        objectsData={satellites}
        objectLat="lat"
        objectLng="lng"
        objectAltitude="alt"
        objectLabel={(d) => (d as ProcessedSatellite).nameForLabel}
        onObjectHover={(obj) => {
          if (globeEl.current && globeEl.current.controls()) {
            (globeEl.current.controls() as any).enabled = !obj;
          }
        }}
        customLayerData={orbits}
        customThreeObject={(d) => {
          const data = d as OrbitRenderData;
          if (!globeEl.current || !data.points || data.points.length < 2) return new THREE.Group();

          const points3D = data.points.map((p) => {
            const { x, y, z } = globeEl.current!.getCoords(p.lat, p.lng, p.alt);
            return new THREE.Vector3(x, y, z);
          });

          return new THREE.LineLoop(
            new THREE.BufferGeometry().setFromPoints(points3D),
            new THREE.LineBasicMaterial({
              color: data.color,
              transparent: true,
              opacity: 0.666,
              linewidth: 1,
            })
          );
        }}
      />
    </div>
  );
};

export default ConstellationGlobe;